/**
 * PricingPage — Public marketing page showing all active subscription plans.
 * Data from /api/public/superadmin/plans + /api/public/superadmin/platform-settings.
 * CTA button routes to /platform-apply with preselected plan.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, ArrowRight, Shield, Zap, Crown, Rocket, Leaf } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audience label per scope — students for schools, delegates for events,
// members for churches, people for ministries. Used in plan limits + hero.
const AUDIENCE_LABEL = {
  school: 'students',
  event: 'delegates',
  church: 'members',
  ministry: 'people',
  standalone: 'users',
};
const SCOPE_NOUN = {
  school: 'school',
  event: 'event',
  church: 'church',
  ministry: 'ministry',
  standalone: 'organisation',
};

const TIER_META = {
  trial:      { icon: Leaf,    iconBg: 'bg-slate-500/20 border-slate-400/30',   iconColor: 'text-slate-300',   tierBadge: 'bg-slate-500/10 text-slate-300 border-slate-400/30',       ring: 'ring-slate-400/20',   cta: 'Start Free Trial', ctaClass: 'bg-slate-500 hover:bg-slate-400 text-white' },
  basic:      { icon: Zap,     iconBg: 'bg-sky-500/20 border-sky-400/30',       iconColor: 'text-sky-300',     tierBadge: 'bg-sky-500/10 text-sky-300 border-sky-400/30',             ring: 'ring-sky-400/20',     cta: 'Start Basic',       ctaClass: 'bg-sky-500 hover:bg-sky-400 text-white' },
  standard:   { icon: Rocket,  iconBg: 'bg-indigo-500/20 border-indigo-400/30', iconColor: 'text-indigo-300',  tierBadge: 'bg-indigo-500/10 text-indigo-300 border-indigo-400/30',    ring: 'ring-indigo-400/30',  cta: 'Start Standard',    ctaClass: 'bg-indigo-500 hover:bg-indigo-400 text-white', popular: true },
  premium:    { icon: Crown,   iconBg: 'bg-fuchsia-500/20 border-fuchsia-400/30', iconColor: 'text-fuchsia-300', tierBadge: 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-400/30', ring: 'ring-fuchsia-400/30', cta: 'Start Premium',     ctaClass: 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white' },
  enterprise: { icon: Shield,  iconBg: 'bg-amber-500/20 border-amber-400/30',   iconColor: 'text-amber-300',   tierBadge: 'bg-amber-500/10 text-amber-300 border-amber-400/30',       ring: 'ring-amber-400/30',   cta: 'Contact Sales',     ctaClass: 'bg-amber-500 hover:bg-amber-400 text-white' },
};

const PricingPage = () => {
  const navigate = useNavigate();

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['public-pricing-plans'],
    queryFn: async () => (await axios.get(`${API_URL}/api/public/superadmin/plans`)).data,
  });

  const { data: settings = {} } = useQuery({
    queryKey: ['public-platform-settings'],
    queryFn: async () => (await axios.get(`${API_URL}/api/public/superadmin/platform-settings`)).data,
  });

  const [scope, setScope] = React.useState(null);
  const scopeOrder = Array.isArray(settings.pricing_scope_order) && settings.pricing_scope_order.length
    ? settings.pricing_scope_order : ['school', 'event', 'church', 'ministry'];
  const availableScopes = Array.from(new Set(plans.map(p => p.scope || 'school')));
  const orderedScopes = scopeOrder.filter(s => availableScopes.includes(s));
  const activeScope = scope || orderedScopes[0] || 'school';
  const scopedPlans = plans.filter(p => (p.scope || 'school') === activeScope);

  // If super admin disabled the pricing page, redirect home
  if (settings && settings.pricing_page_enabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white text-center px-6" data-testid="pricing-page-disabled">
        <div>
          <h1 className="text-2xl font-bold">Pricing is not public right now</h1>
          <p className="text-slate-400 mt-2">Please contact sales for a tailored quote.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-indigo-400 hover:text-indigo-300">
            <ArrowRight className="h-3 w-3 rotate-180" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/60 to-slate-900 text-white" data-testid="pricing-page">
      {/* Hero */}
      <header className="max-w-6xl mx-auto px-4 pt-16 pb-10 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8">
          <ArrowRight className="h-3 w-3 rotate-180" /> Back to Home
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-400/20 text-indigo-300 text-xs uppercase tracking-wider mb-5">
          <Sparkles className="h-3 w-3" /> {settings.platform_tagline || 'Modern School OS'}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-br from-white to-slate-400 bg-clip-text text-transparent">
          Pricing that scales with your {SCOPE_NOUN[activeScope] || 'organisation'}
        </h1>
        <p className="text-slate-400 mt-5 max-w-2xl mx-auto text-base">
          {settings.marketing_tagline || 'Start free. Upgrade when you need more. No credit card required.'}
        </p>

        {/* Scope tabs */}
        {orderedScopes.length > 1 && (
          <div className="mt-8 inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 p-1" role="tablist" data-testid="pricing-scope-tabs">
            {orderedScopes.map(s => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`px-4 py-1.5 text-xs font-medium rounded-full capitalize transition ${activeScope === s ? 'bg-white text-slate-900' : 'text-slate-300 hover:text-white'}`}
                data-testid={`pricing-scope-${s}`}
                role="tab"
              >
                {s === 'church' ? 'Churches' : s === 'ministry' ? 'Ministries' : `${s}s`}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Plans grid */}
      <main className="max-w-6xl mx-auto px-4 pb-20">
        {isLoading && <p className="text-slate-400 text-center">Loading plans…</p>}
        {!isLoading && scopedPlans.length === 0 && (
          <p className="text-slate-400 text-center" data-testid="pricing-empty">
            No public plans are configured for <span className="capitalize">{activeScope === 'church' ? 'Churches' : activeScope === 'ministry' ? 'Ministries' : `${activeScope}s`}</span> yet. Contact sales for a quote.
          </p>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {scopedPlans.map(plan => {
            const meta = TIER_META[plan.tier] || TIER_META.basic;
            const Icon = meta.icon;
            return (
              <Card
                key={plan.code}
                data-testid={`pricing-card-${plan.code}`}
                className={`relative bg-white/5 backdrop-blur-xl border-white/10 text-white overflow-hidden ring-1 ${meta.ring} hover:bg-white/[0.07] transition-colors`}>
                {meta.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white text-[10px] font-bold uppercase tracking-wider text-center py-1">
                    Most popular
                  </div>
                )}
                <CardContent className={`pt-${meta.popular ? '10' : '6'} pb-6 space-y-4`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${meta.iconBg}`}>
                      <Icon className={`h-4 w-4 ${meta.iconColor}`} />
                    </div>
                    <div>
                      <p className="font-bold text-lg leading-tight">{plan.name}</p>
                      <Badge variant="outline" className={`text-[10px] capitalize mt-0.5 ${meta.tierBadge}`}>
                        {plan.tier}
                      </Badge>
                    </div>
                  </div>

                  <div className="py-3 border-y border-white/5">
                    {plan.price_monthly === 0 ? (
                      <p className="text-3xl font-bold">Free</p>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold">${plan.price_monthly}</span>
                          <span className="text-sm text-slate-500">/mo</span>
                        </div>
                        {plan.price_yearly > 0 && (
                          <p className="text-xs text-slate-500 mt-1">or ${plan.price_yearly}/year</p>
                        )}
                      </>
                    )}
                  </div>

                  <p className="text-xs text-slate-400 min-h-[32px]">{plan.description}</p>

                  {/* Limits */}
                  <div className="text-xs space-y-1">
                    <p className="text-slate-300">
                      <Check className="inline h-3 w-3 text-emerald-400 mr-1" />
                      Up to <span className="font-semibold">{plan.max_students ?? '∞'}</span> {AUDIENCE_LABEL[plan.scope || 'school'] || 'users'}
                    </p>
                    <p className="text-slate-300">
                      <Check className="inline h-3 w-3 text-emerald-400 mr-1" />
                      Up to <span className="font-semibold">{plan.max_staff ?? '∞'}</span> staff
                    </p>
                    <p className="text-slate-300">
                      <Check className="inline h-3 w-3 text-emerald-400 mr-1" />
                      <span className="font-semibold">{(plan.modules_enabled || []).length}</span> modules
                    </p>
                    {plan.branding_allowed && (
                      <p className="text-slate-300"><Check className="inline h-3 w-3 text-emerald-400 mr-1" />Custom branding</p>
                    )}
                    {plan.api_access && (
                      <p className="text-slate-300"><Check className="inline h-3 w-3 text-emerald-400 mr-1" />API access</p>
                    )}
                  </div>

                  <Button
                    onClick={() => navigate(`/platform-apply?plan=${plan.code}`)}
                    className={`w-full ${meta.ctaClass}`}
                    data-testid={`pricing-cta-${plan.code}`}>
                    {meta.cta} <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Modules table */}
        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white mt-10">
          <CardContent className="pt-6">
            <h3 className="text-lg font-bold mb-4">What's included in each plan</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                    <th className="text-left p-2">Module</th>
                    {plans.map(p => <th key={p.code} className="text-center p-2 capitalize">{p.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Set(plans.flatMap(p => p.modules_enabled || []))).sort().map(m => (
                    <tr key={m} className="border-b border-white/5">
                      <td className="p-2 capitalize text-slate-300">{m.replace(/_/g, ' ')}</td>
                      {plans.map(p => (
                        <td key={p.code} className="text-center p-2">
                          {(p.modules_enabled || []).includes(m)
                            ? <Check className="h-4 w-4 text-emerald-400 inline" />
                            : <span className="text-slate-600">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Footer CTA */}
        <div className="text-center mt-12 space-y-3">
          <h3 className="text-2xl font-bold">Not sure which plan is right?</h3>
          <p className="text-slate-400 text-sm">
            Start with a {settings.default_trial_days || 14}-day free trial and upgrade anytime.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Link to="/platform-apply">
              <Button size="lg" className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="pricing-footer-cta">
                Apply to Join <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            {settings.support_email && (
              <a href={`mailto:${settings.support_email}`}>
                <Button size="lg" variant="outline" className="text-white border-white/20 bg-white/5 hover:bg-white/10">
                  Talk to Sales
                </Button>
              </a>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
